import api from "../config/api";
import type { ApiResponse, PaginatedResponse } from "../types";

// =====================================================
// 📝 Promotion Types
// =====================================================

export interface Promotion {
  _id: string;
  image: string;
  title: string;
  description: string;
  badge: 'HOT DEAL' | 'SPECIAL' | 'LIMITED' | 'NEW' | 'SALE';
  discountType: 'percentage' | 'fixed';
  discountPercentage: number;
  discountAmount: {
    LAK: number;
    THB: number;
    USD: number;
  };
  applicableTo: 'all' | 'specific_courses' | 'specific_categories';
  courses: string[] | Course[];
  categories: string[] | Category[];
  startDate: string;
  endDate: string;
  isActive: boolean;
  maxUsage: number | null;
  currentUsage: number;
  terms?: string;
  createdBy?: {
    _id: string;
    name: string;
    employeeId?: string;
  };
  createdAt: string;
  updatedAt: string;
  isCurrent?: boolean;
}

interface Course {
  _id: string;
  name: string;
  category?: string;
}

interface Category {
  _id: string;
  name: string;
  description?: string;
}

// =====================================================
// 📝 Request Interfaces
// =====================================================

export interface CreatePromotionRequest {
  title: string;
  description: string;
  image: string;
  badge: 'HOT DEAL' | 'SPECIAL' | 'LIMITED' | 'NEW' | 'SALE';
  discountType: 'percentage' | 'fixed';
  discountPercentage?: number;
  discountAmount?: {
    LAK?: number;
    THB?: number;
    USD?: number;
  };
  applicableTo: 'all' | 'specific_courses' | 'specific_categories';
  courses?: string[];
  categories?: string[];
  startDate: string;
  endDate: string;
  maxUsage?: number | null;
  terms?: string;
}

export interface UpdatePromotionRequest extends Partial<CreatePromotionRequest> {}

export interface GetPromotionsParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean | string;
  applicableTo?: 'all' | 'specific_courses' | 'specific_categories';
  badge?: 'HOT DEAL' | 'SPECIAL' | 'LIMITED' | 'NEW' | 'SALE';
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface ApplyPromotionRequest {
  promotionId: string;
  courseId?: string;
  subtotal: number;
  currency: 'LAK' | 'THB' | 'USD';
}

export interface ApplyPromotionResponse {
  promotion: {
    _id: string;
    title: string;
    discountType: 'percentage' | 'fixed';
    discountPercentage: number;
  };
  subtotal: number;
  discountAmount: number;
  finalAmount: number;
  currency: string;
}

export interface PromotionStats {
  totalPromotions: number;
  activePromotions: number;
  expiredPromotions: number;
  upcomingPromotions: number;
  usageStats: {
    totalUsage: number;
    avgUsage: number;
  };
}

// =====================================================
// 📝 Promotion Service
// =====================================================

export const promotionService = {
  // ດຶງລາຍການໂປຣໂມຊັ່ນທັງໝົດ (Admin)
  getPromotions: async (params: GetPromotionsParams = {}): Promise<PaginatedResponse<Promotion>> => {
    const response = await api.get('/promotions', { params });
    return response.data;
  },

  // ດຶງໂປຣໂມຊັ່ນທີ່ກຳລັງໃຊ້ງານ (Public)
  getActivePromotions: async (): Promise<ApiResponse<Promotion[]>> => {
    const response = await api.get('/promotions/active');
    return response.data;
  },

  // ດຶງຂໍ້ມູນໂປຣໂມຊັ່ນຕາມ ID
  getPromotion: async (id: string): Promise<ApiResponse<Promotion>> => {
    const response = await api.get(`/promotions/${id}`);
    return response.data;
  },

  // ສ້າງໂປຣໂມຊັ່ນໃໝ່
  createPromotion: async (data: CreatePromotionRequest): Promise<ApiResponse<Promotion>> => {
    const response = await api.post('/promotions', data);
    return response.data;
  },

  // ອັບເດດໂປຣໂມຊັ່ນ
  updatePromotion: async (id: string, data: UpdatePromotionRequest): Promise<ApiResponse<Promotion>> => {
    const response = await api.put(`/promotions/${id}`, data);
    return response.data;
  },

  // ລຶບໂປຣໂມຊັ່ນ
  deletePromotion: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/promotions/${id}`);
    return response.data;
  },

  // ເປີດ/ປິດ ສະຖານະໂປຣໂມຊັ່ນ
  togglePromotionStatus: async (id: string): Promise<ApiResponse<Promotion>> => {
    const response = await api.patch(`/promotions/${id}/toggle-status`);
    return response.data;
  },

  // ກວດໂປຣໂມຊັ່ນທີ່ໃຊ້ໄດ້ກັບຄອສ
  getApplicablePromotions: async (courseId: string): Promise<ApiResponse<Promotion[]>> => {
    const response = await api.get(`/promotions/applicable/${courseId}`);
    return response.data;
  },

  // ດຶງສະຖິຕິໂປຣໂມຊັ່ນ
  getPromotionStats: async (): Promise<ApiResponse<PromotionStats>> => {
    const response = await api.get('/promotions/stats');
    return response.data;
  },

  // ຄຳນວນສ່ວນຫຼຸດ (Apply Promotion)
  applyPromotion: async (data: ApplyPromotionRequest): Promise<ApiResponse<ApplyPromotionResponse>> => {
    const response = await api.post('/promotions/apply', data);
    return response.data;
  },
};

// =====================================================
// 📝 Export types
// =====================================================

export type {
  ApiResponse,
  PaginatedResponse,
};